// src/api.js
// ─────────────────────────────────────────────────────────────
//  Central API service for ePayhub Store
//  All fetch calls to the PHP backend go through here.
// ─────────────────────────────────────────────────────────────

const BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:8888';

/** Resolve product/category image paths for local + live hosts */
export function mediaUrl(path) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path) || path.startsWith('data:')) return path;
  const base = BASE_URL.replace(/\/$/, '');
  return `${base}/images/${String(path).replace(/^\//, '')}`;
}

// ── Helper: get auth token from localStorage ─────────────────
export const getToken = () => localStorage.getItem('token');

// ── Helper: get logged-in user ───────────────────────────────
export const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem('currentUser'));
  } catch {
    return null;
  }
};

// ── Helper: check if token is expired ───────────────────────
export const isTokenExpired = () => {
  const token = getToken();
  if (!token) return true;
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    
    const payload = JSON.parse(atob(parts[1]));
    const now = Math.floor(Date.now() / 1000);
    
    // Check if token expires in the next 5 minutes (300 seconds)
    return payload.exp < (now + 300);
  } catch {
    return true;
  }
};

// ── Helper: validate and refresh token on app start ─────────
export const validateAndRefreshToken = async () => {
  const token = getToken();
  const user = getCurrentUser();
  
  if (!token || !user) return false;
  
  if (isTokenExpired()) {
    console.log('🔄 Token expired or expiring soon, refreshing...');
    return await refreshTokenIfNeeded();
  }
  
  return true;
};

// ── Helper: clear session ─────────────────────────────────────
export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('currentUser');
  localStorage.removeItem('userPassword'); // Clear stored password
};

// ── Generic fetch wrapper ─────────────────────────────────────
async function apiFetch(endpoint, options = {}) {
  const token = getToken();

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // Check if token is expired and try to refresh
  if (response.status === 401 && token) {
    console.log('🔄 Token expired, attempting to refresh...');
    const refreshed = await refreshTokenIfNeeded();
    if (refreshed) {
      // Retry the original request with new token
      const newToken = getToken();
      const newHeaders = {
        'Content-Type': 'application/json',
        ...(newToken ? { Authorization: `Bearer ${newToken}` } : {}),
        ...options.headers,
      };
      
      const retryResponse = await fetch(`${BASE_URL}${endpoint}`, {
        ...options,
        headers: newHeaders,
      });
      
      const retryData = await retryResponse.json();
      return retryData;
    }
  }

  const text = await response.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(
      response.ok
        ? 'Invalid server response'
        : `Server error (${response.status}). Is the PHP backend running?`
    );
  }
  if (!response.ok && !data.message) {
    data = { success: false, message: `Request failed (${response.status})`, ...data };
  }
  return data;
}

// ── Auto token refresh function ─────────────────────────────
async function refreshTokenIfNeeded() {
  const currentUser = getCurrentUser();
  if (!currentUser || !currentUser.email) {
    console.log('❌ No user data for token refresh');
    return false;
  }

  // Try to get a fresh token using stored user credentials
  const storedPassword = localStorage.getItem('userPassword');
  
  if (!storedPassword) {
    console.log('❌ No stored credentials for auto-refresh');
    // Don't redirect automatically - let the component handle it
    return false;
  }

  try {
    const response = await fetch(`${BASE_URL}/api/login.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: currentUser.email,
        password: storedPassword
      })
    });

    const data = await response.json();
    
    if (data.success) {
      console.log('✅ Token refreshed automatically');
      localStorage.setItem('token', data.token);
      localStorage.setItem('currentUser', JSON.stringify(data.user));
      window.dispatchEvent(new Event('auth-changed'));
      return true;
    } else {
      console.log('❌ Auto-refresh failed:', data.message);
      // Clear invalid credentials but don't redirect
      localStorage.removeItem('userPassword');
      return false;
    }
  } catch (error) {
    console.error('❌ Auto-refresh error:', error);
    return false;
  }
}

// ─────────────────────────────────────────────────────────────
//  AUTH
// ─────────────────────────────────────────────────────────────

/**
 * Register a new user
 * @param {{ full_name: string, email: string, phone: string, password: string }} body
 */
export async function registerUser(body) {
  return apiFetch('/api/register.php', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Send OTP to email for registration verification
 * @param {object} body - form data (full_name, email, phone, password, etc.)
 */
export async function sendOtp(body) {
  return apiFetch('/api/send-otp.php', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Login
 * @param {{ email: string, password: string }} body
 */
export async function loginUser(body) {
  console.log('🔗 Making login API call to:', `${BASE_URL}/api/login.php`);
  console.log('📤 Request payload:', { ...body, password: '***' });
  
  try {
    const result = await fetch(`${BASE_URL}/api/login.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    
    const data = await result.json();
    console.log('📥 Login API response:', data);
    
    // Store credentials for auto-refresh (encrypted in production)
    if (data.success) {
      localStorage.setItem('userPassword', body.password); // Store for auto-refresh
    }
    
    return data;
  } catch (error) {
    console.error('🚨 Login API error:', error);
    throw error;
  }
}

/**
 * Request password reset email
 * @param {{ email: string }} body
 */
export async function forgotPassword(body) {
  return apiFetch('/api/forgot_password_db.php', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Reset password with token
 * @param {{ token: string, new_password: string }} body
 */
export async function resetPassword(body) {
  return apiFetch('/api/forgot_password_db.php?action=reset', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ─────────────────────────────────────────────────────────────
//  PROFILE
// ─────────────────────────────────────────────────────────────

/** Get logged-in user's profile (requires token) */
export async function getProfile() {
  return apiFetch('/api/profile.php');
}

/**
 * Update profile name/phone (requires token)
 * @param {{ full_name: string, phone: string }} body
 */
export async function updateProfile(body) {
  return apiFetch('/api/profile.php', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Change password (requires token)
 * @param {{ current_password: string, new_password: string }} body
 */
export async function changePassword(body) {
  return apiFetch('/api/profile.php?action=password', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ─────────────────────────────────────────────────────────────
//  ORDERS
// ─────────────────────────────────────────────────────────────

/**
 * Place a new order (works for guests too)
 * @param {object} body  - order details + items array
 */
export async function placeOrder(body) {
  return apiFetch('/api/order.php', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Get all orders for logged-in user (requires token)
 */
export async function getOrders() {
  return apiFetch('/api/order.php');
}

/**
 * Get a single order by ID (requires token)
 * @param {number} id
 */
export async function getOrderById(id) {
  return apiFetch(`/api/order.php?id=${id}`);
}

/**
 * Get order confirmation details (public)
 * @param {string} orderNumber  e.g. "KM1A2B3C4D"
 */
export async function getConfirmation(orderNumber) {
  return apiFetch(`/api/confirmation.php?order_number=${orderNumber}`);
}

/**
 * Get all categories from DB
 */
export async function getCategories() {
  return apiFetch('/api/products.php');
}

/**
 * Get products by category id from DB
 * @param {number} categoryId
 */
export async function getProductsByCategory(categoryId) {
  return apiFetch(`/api/products.php?category=${categoryId}`);
}

/**
 * Get all products from DB
 */
export async function getAllProducts() {
  return apiFetch('/api/products.php?all=1');
}

/**
 * Get all B2B categories from DB
 */
export async function getB2BCategories() {
  return apiFetch('/api/products.php?b2b=1');
}

/**
 * Get B2B products by category id
 * @param {number} categoryId
 */
export async function getB2BProductsByCategory(categoryId) {
  return apiFetch(`/api/products.php?b2b=1&category=${categoryId}`);
}

/**
 * Send phone OTP via Fast2SMS
 * @param {{ phone: string }} body
 */
export async function sendPhoneOtp(body) {
  return apiFetch('/api/sms_send_otp.php', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Verify phone OTP
 * @param {{ phone: string, otp: string }} body
 */
export async function verifyPhoneOtp(body) {
  return apiFetch('/api/sms_verify_otp.php', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Create Omniware payment URL
 */
export async function createPayment(body) {
  return apiFetch('/api/payment.php?action=create', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Verify Omniware payment status
 * @param {{ order_id: string }} body
 */
export async function verifyPayment(body) {
  return apiFetch('/api/payment.php?action=verify', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Update order status (admin only)
 * @param {{ order_id: number, status: string }} body
 */
export async function updateOrderStatus(body) {
  return apiFetch('/api/update-order-status.php', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Get all users (admin only)
 */
export async function getAllUsers() {
  return apiFetch('/api/get-users.php');
}

/**
 * Get all orders – admin sees every order (requires token + admin role)
 */
export async function getAllOrders() {
  return apiFetch('/api/order.php');
}
